import { useState } from "react";
import useModal from "@/hooks/useModal";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import { useMutation } from "@apollo/client";
import { DELETE_ACTIVITY_PARTICIPATIONS } from "../../../../../queries";
import { showSnackbar } from "@/lib/snackbarUtils";

export default function ClearAllParticipantsModal({
  onDismiss,
  activityId,
  participantCount,
  onSuccess
}: {
  onDismiss: () => void;
  activityId: string;
  participantCount: number;
  onSuccess?: () => void | Promise<unknown>;
}) {
  const { Modal, close } = useModal();
  const [bannerError, setBannerError] = useState("");

  const [deleteActivityParticipations, { loading }] = useMutation(DELETE_ACTIVITY_PARTICIPATIONS, {
    onCompleted: async (data) => {
      const result = data?.deleteActivityParticipations;
      if (result?.success) {
        await onSuccess?.();
        showSnackbar(result.message ?? "Participants cleared successfully", { close: true });
        close();
        onDismiss();
      } else {
        setBannerError(result?.message ?? "Failed to clear participants.");
      }
    },
    onError: (error) => {
      setBannerError(error.message ?? "Failed to clear participants.");
    }
  });

  const handleClear = async () => {
    setBannerError("");
    await deleteActivityParticipations({
      variables: {
        activityId
      }
    });
  };

  return (
    <Modal
      onClosed={onDismiss}
      actions={
        <>
          <Button variant="text" onClick={close} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            icon="delete"
            onClick={handleClear}
            loading={loading}
            disabled={loading}
            data-testid="clear-all-participants-confirm-button"
          >
            Clear All Participants
          </Button>
        </>
      }
      headline="Clear All Participants"
      displayOverflow
      className="md:w-[560px]"
    >
      <div className="space-y-4">
        {!!bannerError && (
          <Banner inline variant="error" description={bannerError} closable onClose={() => setBannerError("")} />
        )}
        <p className="text-body-large text-sys-brand-on-surface">
          Are you sure you want to remove all {participantCount} participants from this activity? This will permanently
          delete their activity participations, awarded PDR credits, and any related records. This cannot be undone.
        </p>
      </div>
    </Modal>
  );
}
